import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Eye, Sparkles, Clock, Star, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../data/mock';

export const HeadlightRestoration = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Headlight Restoration Redlands CA | Professional Mobile Headlight Restoration';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional mobile headlight restoration in Redlands, Riverside, and San Bernardino. Starting at $100. Restore cloudy, yellow, oxidized headlights.');
    }

    // Add structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Headlight Restoration",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Precision Mobile Detailing",
        "telephone": businessInfo.phone,
        "priceRange": "$"
      },
      "areaServed": [
        {"@type": "City", "name": "Redlands", "addressRegion": "CA"},
        {"@type": "City", "name": "Loma Linda", "addressRegion": "CA"},
        {"@type": "City", "name": "Yucaipa", "addressRegion": "CA"},
        {"@type": "City", "name": "Rancho Cucamonga", "addressRegion": "CA"},
        {"@type": "City", "name": "Riverside", "addressRegion": "CA"},
        {"@type": "City", "name": "San Bernardino", "addressRegion": "CA"}
      ],
      "offers": {
        "@type": "Offer",
        "price": "100",
        "priceCurrency": "USD",
        "description": "Starting price for headlight restoration services"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const benefits = [
    { icon: Eye, title: 'Improved Clarity', desc: 'Remove oxidation and yellowing to restore clear, bright headlights for better visibility.' },
    { icon: Sparkles, title: 'Enhanced Appearance', desc: 'Transform dull, cloudy headlights to like-new condition, dramatically improving your vehicle appearance.' },
    { icon: Sun, title: 'UV Protection', desc: 'We apply protective coating after restoration to help prevent future yellowing and oxidation.' },
    { icon: Clock, title: 'Cost-Effective', desc: 'Significantly less expensive than replacing headlight assemblies while delivering excellent results.' }
  ];

  const process = [
    'Thorough cleaning to remove surface dirt and contaminants',
    'Sanding with progressively finer grits to remove oxidation layer',
    'Polishing to restore clarity and remove sanding marks',
    'Final cleaning and surface preparation',
    'Application of UV-protective coating to prevent future oxidation',
    'Quality inspection to ensure optimal clarity'
  ];

  const causes = [
    { title: 'UV Exposure', desc: 'Sunlight breaks down the polycarbonate lens material over time, causing yellowing and cloudiness.' },
    { title: 'Oxidation', desc: 'Oxygen reacts with the headlight surface, creating a hazy, discolored layer.' },
    { title: 'Road Debris', desc: 'Small rocks, sand, and debris create micro-scratches that accumulate and reduce clarity.' },
    { title: 'Chemicals', desc: 'Road salt, cleaning products, and environmental chemicals degrade the protective coating.' }
  ];

  const faqs = [
    {
      question: 'How much does headlight restoration cost?',
      answer: 'Headlight restoration is a flat rate of $100 for all vehicles. This includes complete restoration with multi-stage sanding, polishing, and UV-protective coating application.'
    },
    {
      question: 'How long does headlight restoration take?',
      answer: 'Most headlight restoration services take 1-2 hours depending on the condition of the headlights. We work efficiently while ensuring quality results.'
    },
    {
      question: 'How long will restored headlights last?',
      answer: 'With the protective UV coating we apply, restored headlights can remain clear for 1-3 years or more. Regular washing and parking in shade when possible helps extend the results.'
    },
    {
      question: 'Can all headlights be restored?',
      answer: 'Most cloudy, yellow, or oxidized headlights can be successfully restored. However, headlights with severe internal damage, cracks, or moisture inside the housing may require replacement instead of restoration.'
    },
    {
      question: 'Why do headlights turn yellow and cloudy?',
      answer: 'Headlights are made of polycarbonate plastic that degrades from UV exposure, oxidation, road debris, and environmental contaminants over time. This creates the yellow, hazy appearance.'
    },
    {
      question: 'Do you apply protective coating after restoration?',
      answer: 'Yes. We apply a UV-protective coating after restoration to help prevent future yellowing and oxidation. This coating extends the life of the restoration significantly.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Sun className="w-6 h-6" />
              <span className="text-sm font-semibold uppercase tracking-wider">Headlight Restoration Service</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Professional Headlight Restoration
            </h1>
            
            <p className="text-xl mb-6 text-gray-300 leading-relaxed">
              Restore cloudy, yellow, and oxidized headlights to like-new clarity. 
              Improve appearance and visibility with professional mobile headlight restoration.
            </p>

            <div className="inline-block bg-white text-black px-8 py-4 rounded-lg mb-8">
              <p className="text-4xl font-bold mb-1">$100</p>
              <p className="text-sm text-gray-600">Flat rate for all vehicles</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
                <Link to="/booking">
                  Get a Headlight Restoration Quote <ArrowRight className="ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
                <a href={`tel:${businessInfo.phone}`}>
                  <Phone className="mr-2" /> {businessInfo.phone}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Rating • 30 Reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>Mobile Service Available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Causes Yellowing */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">What Causes Headlights to Yellow and Cloud?</h2>
            <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
              Modern headlights are made from durable polycarbonate plastic, but several factors cause them to degrade over time.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {causes.map((cause, idx) => (
                <Card key={idx} className="border">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{cause.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{cause.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Benefits of Professional Headlight Restoration</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, idx) => (
                <Card key={idx} className="border-0 shadow-lg">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mb-6">
                      <benefit.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Restoration Process</h2>
            <p className="text-lg text-gray-600 mb-8">
              We use professional techniques and products to safely restore clarity without damaging your headlight housing.
            </p>
            
            <div className="space-y-4">
              {process.map((step, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    {idx + 1}
                  </div>
                  <p className="text-gray-700 pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* When to Restore vs Replace */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Restoration vs Replacement</h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                <strong>Headlight restoration is ideal when:</strong>
              </p>
              <ul className="list-none space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span>Headlights are cloudy, yellow, or oxidized on the surface</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span>The lens is structurally sound without cracks or deep damage</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span>There is no moisture or fogging inside the housing</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-black flex-shrink-0 mt-1" />
                  <span>You want a cost-effective alternative to expensive replacements</span>
                </li>
              </ul>

              <p className="mt-6">
                <strong>Replacement may be necessary when:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Headlight lens is cracked or has deep gouges</li>
                <li>Moisture is trapped inside the housing</li>
                <li>Internal reflectors are damaged or degraded</li>
                <li>The housing itself is broken or warped</li>
              </ul>

              <p className="mt-6">
                We evaluate your headlights during the quote process and provide honest recommendations on whether restoration 
                or replacement is the best option for your situation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Areas We Serve</h2>
            <p className="text-lg text-gray-600 mb-8">
              Mobile headlight restoration throughout the Inland Empire
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/service-areas/redlands-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Redlands
              </Link>
              <Link to="/service-areas/loma-linda-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Loma Linda
              </Link>
              <Link to="/service-areas/yucaipa-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Yucaipa
              </Link>
              <Link to="/service-areas/rancho-cucamonga-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Rancho Cucamonga
              </Link>
              <Link to="/service-areas/riverside-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                Riverside
              </Link>
              <Link to="/service-areas/san-bernardino-ca" className="bg-gray-100 px-6 py-3 rounded-full font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors">
                San Bernardino
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border shadow-sm">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3">{faq.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Restore Your Headlights?</h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto text-gray-300">
            Get a quote for professional headlight restoration. Improve appearance and clarity today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-8 py-6">
              <Link to="/booking">
                Get a Quote <ArrowRight className="ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white text-white hover:bg-white hover:text-black transition-all duration-300">
              <Link to="/services">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HeadlightRestoration;
